import React, { useState, useEffect } from 'react';
import { supabase } from '../lib/supabase';
import { User } from '@supabase/supabase-js';

interface UserAvatarProps {
  user: User | null;
  size?: 'sm' | 'md' | 'lg';
  className?: string;
}

const UserAvatar: React.FC<UserAvatarProps> = ({ user, size = 'md', className = '' }) => {
  const [avatar, setAvatar] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  // Tamaños predefinidos
  const sizeClasses = {
    sm: 'w-8 h-8 text-sm',
    md: 'w-12 h-12 text-xl',
    lg: 'w-16 h-16 text-2xl'
  };

  useEffect(() => {
    if (user) {
      getAvatar(user.id);
    } else {
      setLoading(false);
    }
  }, [user]);

  const getAvatar = async (userId: string) => {
    try {
      setLoading(true);
      // Comprobar si existe el avatar
      const { data: existingAvatar, error: existingError } = await supabase
        .storage
        .from('curriculab')
        .list(`avatars/${userId}`);
      
      if (existingError) throw existingError;
      
      if (existingAvatar && existingAvatar.length > 0) {
        const { data: { publicUrl } } = supabase
          .storage
          .from('curriculab')
          .getPublicUrl(`avatars/${userId}/${existingAvatar[0].name}`);
          
        setAvatar(publicUrl);
      }
    } catch (error) {
      console.error('Error al obtener avatar:', error);
    } finally {
      setLoading(false);
    }
  };

  const getUserInitial = () => {
    if (!user) return '?';
    
    const displayName = user.user_metadata?.full_name || 
                        user.email?.split('@')[0] || 
                        'Usuario';
    
    return displayName.charAt(0).toUpperCase();
  };

  // Aplicar estilo personalizado para asegurar forma circular
  const containerStyle: React.CSSProperties = {
    borderRadius: '9999px',
    overflow: 'hidden',
    position: 'relative',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center'
  };

  const imageStyle: React.CSSProperties = {
    width: '100%',
    height: '100%',
    objectFit: 'cover',
    borderRadius: '9999px'
  };

  return (
    <div className={`relative ${className}`} style={{ borderRadius: '9999px' }}>
      {/* Avatar o inicial */}
      <div 
        className={`${sizeClasses[size]} rounded-full flex items-center justify-center text-white font-bold shadow-lg`}
        style={containerStyle}
      >
        {loading ? (
          <div className="animate-pulse bg-gradient-to-br from-indigo-600 to-purple-700 w-full h-full rounded-full"></div>
        ) : avatar ? (
          <img 
            src={avatar} 
            alt="Avatar" 
            className="w-full h-full object-cover rounded-full"
            style={imageStyle}
            onError={() => setAvatar(null)} // En caso de error al cargar la imagen
          />
        ) : (
          <div className="bg-gradient-to-br from-indigo-600 to-purple-700 w-full h-full flex items-center justify-center rounded-full">
            {getUserInitial()}
          </div>
        )}
      </div>
      
      {/* Efecto de resplandor al pasar el cursor */}
      <div className="absolute inset-0 rounded-full border-2 border-purple-500/50 opacity-0 hover:opacity-100 transition-opacity duration-300"></div>
    </div>
  );
};

export default UserAvatar; 